import { Box, Button, FormControl, MenuItem, Typography } from "@mui/material";
import { AppSelect } from "components/common/Input";
import { MenuProps } from "constants/filterMenuProps";
import useSearchParamsOperations from "hooks/useSearchParamsOperations";
import { useCallback, useEffect, useState } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import { useSearchParams } from "react-router-dom";
import { Location } from "types/location";
import { Pickup, PickupStatuses } from "types/pickup";
import { formatDate } from "utils/helpers";

interface PickupFiltersProps {
  filterPickups: Pickup[];
  pickups: Pickup[];
  locations: Location[];
  isLoading: boolean;
  hasNextPage: boolean;
  errorMessage: string | null;
  incrementCurrentPage: () => void;
}

const pickupStatusOptions = PickupStatuses.map((status) => (
  <MenuItem key={status.value} value={status.value}>
    {status.label}
  </MenuItem>
));

export default function PickupFilters({
  filterPickups,
  pickups,
  locations,
  isLoading,
  hasNextPage,
  errorMessage,
  incrementCurrentPage,
}: PickupFiltersProps) {
  const { deleteFieldInArrayUsingPrefix, replaceInArrayFieldUsingPrefix } = useSearchParamsOperations();
  const [sentryRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage,
    onLoadMore: incrementCurrentPage,
    disabled: !!errorMessage,
    rootMargin: "0px 0px 400px 0px",
  });

  const [searchParams] = useSearchParams();

  const [pickupIds, setPickupIds] = useState(
    searchParams
      .getAll("filter[]")
      .find((p) => p.includes("id,is_any_of"))
      ?.split("id,is_any_of,")[1]
      ?.split(",") || []
  );
  const [locationId, setLocationId] = useState(
    searchParams
      .getAll("filter[]")
      .find((p) => p.includes("location_id,is"))
      ?.split("location_id,is,")[1] || ""
  );
  const [states, setStates] = useState(
    searchParams
      .getAll("filter[]")
      .find((p) => p.includes("state,is_any_of"))
      ?.split("state,is_any_of,")[1]
      ?.split(",") || []
  );

  const cleanFilters = useCallback(() => {
    setPickupIds([]);
    setLocationId("");
    setStates([]);
  }, []);

  useEffect(() => {
    if (pickupIds && pickupIds.length > 0) {
      replaceInArrayFieldUsingPrefix("filter[]", "id,is_any_of", `id,is_any_of,${pickupIds.join(",")}`);
    } else {
      deleteFieldInArrayUsingPrefix("filter[]", "id,is_any_of");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickupIds]);

  useEffect(() => {
    if (locationId && locationId.length > 0) {
      replaceInArrayFieldUsingPrefix("filter[]", "location_id,is", `location_id,is,${locationId}`);
    } else {
      deleteFieldInArrayUsingPrefix("filter[]", "location_id,is");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  useEffect(() => {
    if (states && states.length > 0) {
      replaceInArrayFieldUsingPrefix("filter[]", "state,is_any_of", `state,is_any_of,${states.join(",")}`);
    } else {
      deleteFieldInArrayUsingPrefix("filter[]", "state,is_any_of");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states]);

  return (
    <Box
      display="grid"
      gridTemplateColumns="auto 1fr 1fr 1fr 1fr 1fr auto"
      columnGap="1em"
      marginY={2}
      alignItems="center"
    >
      <Typography width="100%">Quick Filters:</Typography>
      <FormControl variant="standard" size="small" sx={{ width: "100%" }}>
        <AppSelect
          multiple
          MenuProps={MenuProps}
          value={pickupIds}
          onChange={(e) => setPickupIds(e.target.value as string[])}
          renderValue={(values) => (
            <Box display="flex" alignItems="center" gap={0.5} overflow="hidden">
              <Typography noWrap color={values.length === 0 ? "text.secondary" : undefined}>
                {values.length === 0
                  ? "Pickup Date"
                  : values.length === 1
                  ? formatDate(pickups.find(({ id }) => id.toString() === values[0].toString())?.scheduledAt || "")
                  : `${values.length} dates selected`}
              </Typography>
            </Box>
          )}
          displayEmpty
        >
          {filterPickups
            .filter(({ scheduledAt }) => scheduledAt)
            .map((pickup) => (
              <MenuItem key={pickup.id} value={pickup.id} ref={sentryRef}>
                {formatDate(pickup.scheduledAt)}- {pickup.orderNumber}
              </MenuItem>
            ))}
        </AppSelect>
      </FormControl>
      <FormControl variant="standard" size="small" sx={{ width: "100%" }}>
        <AppSelect
          MenuProps={MenuProps}
          value={locationId}
          onChange={(e) => setLocationId(e.target.value.toString())}
          displayEmpty
          renderValue={(value) => (
            <Box display="flex" alignItems="center" gap={0.5} overflow="hidden">
              <Typography noWrap color={value ? undefined : "text.secondary"}>
                {locations.find(({ id }) => id?.toString() === value)?.name || "Location"}
              </Typography>
            </Box>
          )}
        >
          <MenuItem value="">Location</MenuItem>
          {locations.map((location) => (
            <MenuItem key={location.id} value={location.id}>
              {location.name}
            </MenuItem>
          ))}
        </AppSelect>
      </FormControl>

      <FormControl variant="standard" size="small" sx={{ width: "100%" }}>
        <AppSelect
          multiple
          MenuProps={MenuProps}
          value={states}
          onChange={(e) => setStates(e.target.value as string[])}
          displayEmpty
          renderValue={(values) => (
            <Box display="flex" alignItems="center" gap={0.5} overflow="hidden">
              <Typography noWrap color={values.length === 0 ? "text.secondary" : undefined}>
                {values.length === 0
                  ? "Pickup Status"
                  : values.length === 1
                  ? PickupStatuses.find(({ value }) => value === values[0])?.label
                  : `${values.length} statuses selected`}
              </Typography>
            </Box>
          )}
        >
          {pickupStatusOptions}
        </AppSelect>
      </FormControl>

      <Button variant={"outlined"} onClick={cleanFilters}>
        Clear All
      </Button>
    </Box>
  );
}
